import { Reorder } from 'framer-motion';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import dotsGrid from 'src/assets/icons/dots-grid-06-icon.svg';
import CommonModal from 'src/components/CommonModal';
import Toggle from 'src/components/Toggle/Toggle';

import { IWidget, IWidgetSettingsModalProps } from './widgetSettings.interface';

const WidgetSettingsModal: FC<IWidgetSettingsModalProps> = ({ handleClose, widgets, onSave, title = 'Widgets', filterPredicate, minEnabled = 0 }) => {
    const [widgetsData, setWidgetsData] = useState<IWidget[]>(widgets);
    const [isLoading, setIsLoading] = useState(false);

    // Filter widgets if a filter predicate is provided
    const filteredWidgets = useMemo(() => {
        if (!filterPredicate) return widgets;

        return widgets.filter(filterPredicate);
    }, [widgets, filterPredicate]);

    useEffect(() => {
        setWidgetsData(filteredWidgets);
    }, [filteredWidgets]);

    const hasChanges = useMemo(() => JSON.stringify(widgetsData) !== JSON.stringify(filteredWidgets), [widgetsData, filteredWidgets]);

    const enabledCount = useMemo(() => widgetsData?.filter((w) => w?.isEnabled)?.length ?? 0, [widgetsData]);

    const handleToggle = useCallback(
        (id: string) => () => {
            setWidgetsData((prevWidgets) => prevWidgets.map((widget) => (widget.id === id ? { ...widget, isEnabled: !widget.isEnabled } : widget)));
        },
        []
    );

    const handleSave = useCallback(async () => {
        setIsLoading(true);

        try {
            // Update positions based on current order
            const widgetsWithUpdatedPositions = widgetsData.map((widget, index) => ({
                ...widget,
                position: index,
            }));

            await onSave(widgetsWithUpdatedPositions);
            handleClose();
        } catch (error) {
            console.error('Failed to update widget settings:', error);
        } finally {
            setIsLoading(false);
        }
    }, [widgetsData, onSave, handleClose]);

    return (
        <CommonModal
            title={title}
            handleClose={handleClose}
            size={'max-w-[530px]'}
            primaryBtnText="Save"
            primaryBtnOnClick={handleSave}
            primaryBtnDisabled={isLoading || !hasChanges}
            primaryBtnLoading={isLoading}
            secondaryBtnText="Cancel"
            secondaryBtnOnClick={handleClose}
        >
            <div className="p-4">
                <Reorder.Group axis="y" values={widgetsData} onReorder={setWidgetsData} className="space-y-3 bg-gray25 border border-utilityGray200 rounded-md p-4">
                    {widgetsData.map((widget) => {
                        const isToggleDisabled = widget?.isEnabled && enabledCount <= minEnabled;
                        const Icon = widget?.icon;

                        return (
                            <Reorder.Item key={widget?.id} value={widget} className="cursor-grab pb-3 border-b border-utilityGray200 last:border-0 last:pb-0">
                                <div className="flex items-center gap-x-2">
                                    <div className="flex items-center gap-x-2 flex-1">
                                        {Icon && <Icon className="w-5 h-5 text-gray400 flex-none" />}
                                        <p className="text-textSecondary text-xs leading-[18px] font-medium flex-1">{widget?.title}</p>
                                    </div>
                                    <div className="flex items-center flex-none gap-x-1.5">
                                        <Toggle
                                            isChecked={widget?.isEnabled}
                                            onChange={handleToggle(widget?.id)}
                                            isDisabled={isToggleDisabled}
                                            labelClassName={`!items-end`}
                                            labelTextClassName={'!hidden'}
                                            name={`widget-toggle-${widget?.id}`}
                                            labelPosition="right"
                                            className={`${isToggleDisabled ? ' !cursor-not-allowed' : ''} flex-none`}
                                        />
                                        <div className="flex-none">
                                            <img src={dotsGrid} alt="Drag handle" className="w-5 h-5" draggable={false} />
                                        </div>
                                    </div>
                                </div>
                            </Reorder.Item>
                        );
                    })}
                </Reorder.Group>
            </div>
        </CommonModal>
    );
};

export default WidgetSettingsModal;
